/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { breakpoints } from '@coursera/cds-core';

import Badge from 'bundles/unified-description-page-common/components/shared/badges/Badge';
import NewAiBadge from 'bundles/unified-description-page-common/components/shared/badges/NewAiBadge';
import { ENROLLMENT_COUNT_THRESHOLD } from 'bundles/unified-description-page-common/constants';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import { useSavedProduct } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusSavedProductContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import { shouldShowNewAiSkillsBadge } from 'bundles/unified-description-page-common/utils/shouldShowNewAiSkillsBadge';

import _t from 'i18n!nls/unified-description-page-common';

const styles = {
  container: css`
    display: flex;
    height: 40px;
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
    gap: var(--cds-spacing-100);
    top: calc(var(--cds-spacing-300) * -1);
    right: 0;
    z-index: 1;

    ${breakpoints.down('sm')} {
      position: static;
      margin-bottom: var(--cds-spacing-200);
      flex-wrap: wrap;
    }
  `,
  savedProductManager: css`
    top: 0;
  `,
};

export const mapPageDataToHeroBadges = (
  pageData:
    | ConsumerSpecializationPageFieldsFragment
    | ConsumerCoursePageFieldsFragment
    | EnterpriseSpecializationPageFieldsFragment
    | EnterpriseCoursePageFieldsFragment
): Props => {
  return {
    enrollmentCount:
      Number(pageData?.totalEnrollmentCount) > ENROLLMENT_COUNT_THRESHOLD ? pageData.totalEnrollmentCount : undefined,
    topInstructor: pageData.instructors?.some((instructor) => instructor.isTopInstructor),
    newAISkills: shouldShowNewAiSkillsBadge(pageData.slug),
    slug: pageData.slug,
  };
};

export type Props = {
  enrollmentCount?: number | null;
  topInstructor?: boolean;
  newAISkills?: boolean;
  slug?: string;
};

export function HeroBadges({ enrollmentCount, topInstructor, newAISkills, slug }: Props) {
  const { enableXDPRedesignPhase1, enableXDPRedesignHero } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignHero;
  const hasSavedProductManager = !!useSavedProduct()?.saveProductManager;
  const isSubscribedToCPlusOrLite = !!useCourseraPlusOwnership()?.isSubscribedToCPlusOrLite;

  if (!enableXDPRedesign) {
    return null;
  }

  const badges = [];
  if (!enableXDPRedesignHero && enrollmentCount) {
    badges.push(
      <Badge designStyle="thick">
        <FormattedMessage
          message={_t('{enrollmentCount} enrolled')}
          enrollmentCount={
            <strong>
              <FormattedNumber value={enrollmentCount} />
            </strong>
          }
        />
      </Badge>
    );
  }
  if (topInstructor) {
    badges.push(<Badge designStyle="thick">{_t('Top instructor')}</Badge>);
  }
  if (newAISkills) {
    badges.push(<NewAiBadge slug={slug} designStyle="thick" />);
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <div css={[styles.container, isSubscribedToCPlusOrLite && hasSavedProductManager && styles.savedProductManager]}>
      {badges}
    </div>
  );
}

export default HeroBadges;
